/**
 * Fixed-capacity rolling PCM16 byte buffer for debug playback.
 *
 * Each `AudioChannel` keeps one of these and taps it after the
 * pipeline has produced the PCM16 chunk that's about to go out the
 * WebSocket — i.e. the buffer holds *exactly* the bytes Deepgram
 * sees. When the user wants to hear what's being shipped, they
 * dump the buffer as a WAV file and play it in any audio player.
 *
 * Storage is a single contiguous Uint8Array used as a circular
 * buffer. Append is O(N) in chunk size but allocation-free; dump
 * copies the buffer into chronological order once. That trade is
 * deliberate — the hot path runs every ~10 ms and must not cause
 * GC pressure, while dumping happens at most a couple of times per
 * session and the copy cost is irrelevant there.
 *
 * NOT a transport replacement: this buffer is for debugging only.
 * The contents are never sent over the wire and the buffer is
 * eagerly trimmed to the configured capacity, so a long meeting
 * occupies a fixed ~960 KB / channel (30 s mono 16-bit @ 16 kHz)
 * regardless of session length.
 */

/**
 * Build a 44-byte canonical PCM WAV header for `dataBytes` of int16
 * mono / stereo samples at `sampleRate`. The result is the literal
 * RIFF/WAVE/fmt /data prefix you can prepend to raw PCM16 to get a
 * valid file every audio player accepts.
 */
function buildWavHeader(dataBytes: number, sampleRate: number, numChannels: number): Uint8Array {
  const bitsPerSample = 16
  const byteRate = (sampleRate * numChannels * bitsPerSample) / 8
  const blockAlign = (numChannels * bitsPerSample) / 8
  const fileSize = 36 + dataBytes

  const header = new ArrayBuffer(44)
  const view = new DataView(header)
  // "RIFF" — chunk id
  view.setUint8(0, 0x52)
  view.setUint8(1, 0x49)
  view.setUint8(2, 0x46)
  view.setUint8(3, 0x46)
  // chunk size = file size - 8
  view.setUint32(4, fileSize, true)
  // "WAVE" — format
  view.setUint8(8, 0x57)
  view.setUint8(9, 0x41)
  view.setUint8(10, 0x56)
  view.setUint8(11, 0x45)
  // "fmt " — sub-chunk 1 id
  view.setUint8(12, 0x66)
  view.setUint8(13, 0x6d)
  view.setUint8(14, 0x74)
  view.setUint8(15, 0x20)
  // sub-chunk 1 size (16 for PCM)
  view.setUint32(16, 16, true)
  // audio format (1 = PCM linear)
  view.setUint16(20, 1, true)
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitsPerSample, true)
  // "data" — sub-chunk 2 id
  view.setUint8(36, 0x64)
  view.setUint8(37, 0x61)
  view.setUint8(38, 0x74)
  view.setUint8(39, 0x61)
  view.setUint32(40, dataBytes, true)
  return new Uint8Array(header)
}

export interface PcmRingBufferOptions {
  /** Max audio duration to keep, in seconds. Multiplied by
   *  `sampleRate * 2` (mono int16) to derive the byte capacity. If
   *  the producer's actual rate diverges from `sampleRate`, capacity
   *  in *seconds of audio* will diverge proportionally — that's
   *  intentional (raw debug taps configure a generous nominal rate
   *  and let the actual rate be reported at dump time). */
  maxSeconds: number
  /** Nominal sample rate in Hz. Used both for capacity sizing AND
   *  as the default WAV header rate when `toWav()` is called without
   *  an override. The post-resample tap configures this to
   *  TARGET_SAMPLE_RATE (16000); raw taps configure it to whatever
   *  the source claims. */
  sampleRate: number
}

export class PcmRingBuffer {
  private readonly buffer: Uint8Array
  /** Write position (next byte to overwrite). Wraps at `buffer.length`. */
  private writePos = 0
  /** Total bytes written across the lifetime of this buffer.
   *  `min(written, buffer.length)` is the count of valid bytes
   *  currently in the buffer; `written > buffer.length` means we've
   *  wrapped at least once. */
  private written = 0
  readonly sampleRate: number
  readonly capacityBytes: number

  constructor(opts: PcmRingBufferOptions) {
    if (opts.maxSeconds <= 0 || !Number.isFinite(opts.maxSeconds)) {
      throw new Error(`PcmRingBuffer: invalid maxSeconds ${opts.maxSeconds}`)
    }
    if (opts.sampleRate <= 0 || !Number.isFinite(opts.sampleRate)) {
      throw new Error(`PcmRingBuffer: invalid sampleRate ${opts.sampleRate}`)
    }
    this.sampleRate = opts.sampleRate
    // Mono int16 → 2 bytes/sample.
    this.capacityBytes = Math.ceil(opts.maxSeconds * opts.sampleRate * 2)
    this.buffer = new Uint8Array(this.capacityBytes)
  }

  /** Append a chunk of PCM16 bytes. Single-pass write that wraps
   *  at the buffer boundary. Chunk size > capacity is allowed; only
   *  the trailing `capacityBytes` are kept. */
  append(pcm: ArrayBuffer | ArrayBufferView): void {
    const src =
      pcm instanceof ArrayBuffer ? new Uint8Array(pcm) : new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength)
    if (src.length === 0) return

    // Oversize chunks: only the last `capacityBytes` matter. Drop
    // the head and treat the tail as a fresh fill.
    if (src.length >= this.capacityBytes) {
      const tail = src.subarray(src.length - this.capacityBytes)
      this.buffer.set(tail, 0)
      this.writePos = 0
      this.written += src.length
      return
    }

    const cap = this.capacityBytes
    const firstRun = Math.min(src.length, cap - this.writePos)
    this.buffer.set(src.subarray(0, firstRun), this.writePos)
    if (firstRun < src.length) {
      this.buffer.set(src.subarray(firstRun), 0)
      this.writePos = src.length - firstRun
    } else {
      this.writePos = (this.writePos + firstRun) % cap
    }
    this.written += src.length
  }

  /** Bytes currently held in the buffer (≤ capacityBytes). */
  bytesBuffered(): number {
    return Math.min(this.written, this.capacityBytes)
  }

  /** Audio duration currently held, in seconds. Mono int16 → 2 B/sample. */
  secondsBuffered(): number {
    return this.bytesBuffered() / (this.sampleRate * 2)
  }

  /** Snapshot the buffer in chronological order. Returns a fresh
   *  Uint8Array so callers can transfer it across worker / IPC
   *  boundaries without aliasing the live ring. Returns an empty
   *  array when nothing has been written yet. */
  snapshot(): Uint8Array {
    const valid = this.bytesBuffered()
    if (valid === 0) return new Uint8Array(0)
    const out = new Uint8Array(valid)
    if (this.written < this.capacityBytes) {
      // Haven't wrapped: data is contiguous from index 0 to writePos.
      out.set(this.buffer.subarray(0, valid))
    } else {
      // Have wrapped: oldest byte is at writePos. Copy
      // [writePos, end) then [0, writePos).
      const tail = this.buffer.subarray(this.writePos)
      const head = this.buffer.subarray(0, this.writePos)
      out.set(tail, 0)
      out.set(head, tail.length)
    }
    return out
  }

  /** Snapshot + WAV-encode in one shot. Returns a fresh Uint8Array
   *  ready to write to disk or wrap in a Blob — header + data, no
   *  separate metadata channel needed. Empty when no audio is
   *  buffered.
   *
   *  `overrideSampleRate` lets the caller mark the WAV with a rate
   *  different from the buffer's nominal one. Required for raw
   *  debug taps where the producer's rate is *measured* (and only
   *  becomes known after a few hundred ms of capture) while the
   *  buffer was sized using the producer's *claimed* rate. Pass the
   *  measured rate so the WAV plays back at the right speed. */
  toWav(overrideSampleRate?: number): Uint8Array {
    const data = this.snapshot()
    if (data.length === 0) return new Uint8Array(0)
    const sr = overrideSampleRate && overrideSampleRate > 0 ? overrideSampleRate : this.sampleRate
    const header = buildWavHeader(data.length, sr, 1)
    const wav = new Uint8Array(header.length + data.length)
    wav.set(header, 0)
    wav.set(data, header.length)
    return wav
  }

  /** Forget all buffered audio. Use after a meaningful state change
   *  (e.g. capture restart on a different device) so the next dump
   *  doesn't blend the old device's audio with the new. */
  clear(): void {
    this.writePos = 0
    this.written = 0
    // Don't bother zeroing `buffer` — `bytesBuffered()` gates reads,
    // so stale data is unreachable until it's overwritten.
  }
}
